import axios from "axios";
import { useEffect, useState, useRef, useMemo } from "react";
import {
  Linkedin,
  Loader2,
  Send,
  Bot,
  BotOff,
  Inbox,
  MessageSquare,
  Sparkles,
  Flame,
  ArrowUpDown,
  UserCheck,
  Clock,
  CheckCircle2,
  XCircle,
  MessageCircle,
  UserSearch,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import SourcingLinkedInPage from "@/pages/SourcingLinkedInPage";

// ─── Maya interest badge (iter67) ────────────────────────────────────
// Visual signal of the candidate's last-message intent, computed by
// Claude on every inbound webhook. See `linkedin_inmail_ai.classify_interest_signal`.

const INTEREST_STYLES = {
  hot:   { label: "Quente", emoji: "🔥", cls: "bg-rose-100 text-rose-700 border-rose-200", sort: 0 },
  warm:  { label: "Morno",  emoji: "🟢", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", sort: 1 },
  cold:  { label: "Frio",   emoji: "❄️", cls: "bg-slate-100 text-slate-600 border-slate-200", sort: 2 },
  noise: { label: "Ruído",  emoji: "🚫", cls: "bg-slate-50 text-slate-400 border-slate-200", sort: 3 },
};

// Maya operational status (iter75) — what happened in her last reply attempt.
// Surfaced as a banner inside the active conversation so the recruiter sees
// WHY a reply didn't go out (was silent failure before iter75).
const MAYA_STATUS_STYLES = {
  replied:            { label: "Respondeu com sucesso", emoji: "✅",
                        cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  handed_off:         { label: "Pausou e passou pra você", emoji: "🤚",
                        cls: "bg-amber-50 text-amber-900 border-amber-200" },
  generation_failed:  { label: "Falhou ao gerar resposta", emoji: "⚠️",
                        cls: "bg-rose-50 text-rose-800 border-rose-200" },
  send_failed:        { label: "Falhou ao enviar pelo LinkedIn", emoji: "📡",
                        cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

const InterestBadge = ({ level, reason }) => {
  const style = INTEREST_STYLES[level];
  if (!style) return null;
  return (
    <span
      data-testid={`interest-badge-${level}`}
      title={reason || ""}
      className={`text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${style.cls}`}
    >
      {style.emoji} {style.label}
    </span>
  );
};

const INVITE_STATUS = {
  pending:  { label: "Pendente",  icon: Clock,        cls: "text-amber-600 bg-amber-50 border-amber-200" },
  accepted: { label: "Aceito",    icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  declined: { label: "Recusado",  icon: XCircle,      cls: "text-rose-600 bg-rose-50 border-rose-200" },
};

const LinkedInInboxPage = () => {
  const [activeTab, setActiveTab] = useState("sourcing");

  // Chat tab state
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChatId, setActiveChatId] = useState("");
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sortByInterest, setSortByInterest] = useState(false);
  const [mayaRetrying, setMayaRetrying] = useState(false);
  const messagesEndRef = useRef(null);

  // Invites tab state
  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [syncingInvites, setSyncingInvites] = useState(false);
  const [acceptingInvite, setAcceptingInvite] = useState(null);

  // Order: hot → warm → cold → noise → uncategorized → then by recency
  const sortedThreads = useMemo(() => {
    if (!sortByInterest) return threads;
    const rank = (t) => INTEREST_STYLES[t.interest_level]?.sort ?? 99;
    return [...threads].sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      return (b.last_message_at || "").localeCompare(a.last_message_at || "");
    });
  }, [threads, sortByInterest]);

  const activeContact = activeThread?.attendee_name || "";
  useDocumentTitle(
    activeContact ? `${activeContact} · LinkedIn` : "InMails LinkedIn",
  );

  const loadThreads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/integrations/linkedin/threads`);
      setThreads(res.data.threads || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar conversas");
    } finally {
      setLoading(false);
    }
  };

  const loadInvites = async () => {
    setLoadingInvites(true);
    try {
      const res = await axios.get(`${API}/integrations/linkedin/invites`);
      setInvites(res.data.invites || []);
    } catch (err) {
      console.error("Erro ao carregar convites:", err);
    } finally {
      setLoadingInvites(false);
    }
  };

  const handleSyncInvites = async () => {
    setSyncingInvites(true);
    try {
      const res = await axios.post(`${API}/integrations/linkedin/invites/sync`);
      const { updated } = res.data;
      if (updated > 0) {
        toast.success(`${updated} convite(s) atualizado(s) com sucesso!`);
        await loadInvites();
      } else {
        toast.info("Nenhum novo aceite encontrado no Synkly.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao sincronizar convites");
    } finally {
      setSyncingInvites(false);
    }
  };

  const handleManualAccept = async (inv) => {
    setAcceptingInvite(inv.id);
    try {
      const res = await axios.post(`${API}/integrations/linkedin/invites/${inv.id}/accept`);
      const { maya_triggered } = res.data;
      if (maya_triggered) {
        toast.success(`Convite aceito! Maya vai enviar a mensagem inicial para ${inv.attendee_name || "o candidato"}.`);
      } else {
        toast.success(`Convite de ${inv.attendee_name || "candidato"} marcado como aceito.`);
      }
      await loadInvites();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao marcar como aceito");
    } finally {
      setAcceptingInvite(null);
    }
  };

  useEffect(() => {
    loadThreads();
    loadInvites();
    const t = setInterval(loadThreads, 30000);
    const t2 = setInterval(loadInvites, 60000);
    return () => { clearInterval(t); clearInterval(t2); };
  }, []);

  const loadThread = async (chatId) => {
    setActiveChatId(chatId);
    setLoadingMessages(true);
    setDraft("");
    try {
      const res = await axios.get(
        `${API}/integrations/linkedin/threads/${chatId}`,
      );
      setActiveThread(res.data.thread);
      setMessages(res.data.messages || []);
      // refresh threads list to clear unread badge
      loadThreads();
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao abrir conversa");
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSend = async () => {
    if (!draft.trim() || !activeChatId) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/integrations/linkedin/threads/${activeChatId}/reply`,
        { text: draft.trim(), pause_maya: true },
      );
      setDraft("");
      await loadThread(activeChatId);
      toast.success("Mensagem enviada. Maya pausada nesta conversa.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setSending(false);
    }
  };

  const handleToggleMaya = async () => {
    if (!activeThread) return;
    const newState = !activeThread.maya_enabled;
    try {
      await axios.post(
        `${API}/integrations/linkedin/threads/${activeChatId}/maya-toggle?enabled=${newState}`,
      );
      toast.success(newState ? "Maya ativada" : "Maya pausada");
      await loadThread(activeChatId);
    } catch (err) {
      toast.error("Falha ao alterar Maya");
    }
  };

  // Force Maya to retry replying to the last inbound message — useful when
  // the previous attempt failed silently (LLM glitch, Unipile rate limit).
  const handleMayaRetry = async () => {
    if (!activeChatId) return;
    setMayaRetrying(true);
    try {
      const res = await axios.post(
        `${API}/integrations/linkedin/chats/${activeChatId}/maya-retry`,
      );
      if (res.data.status === "replied") {
        toast.success("Maya respondeu agora!");
      } else if (res.data.status === "handed_off") {
        toast.info("Maya pausou — assuma a conversa.");
      } else {
        toast.error(
          `Maya falhou de novo: ${res.data.error || res.data.status}`,
        );
      }
      await loadThread(activeChatId);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Falha ao retentar Maya.",
      );
    } finally {
      setMayaRetrying(false);
    }
  };

  return (
    <div data-testid="linkedin-inbox-page" className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
          Comunicação · LinkedIn
        </div>
        <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">
          LinkedIn
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Mensagens diretas e convites de conexão do LinkedIn, gerenciados pela Maya.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("sourcing")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "sourcing"
              ? "border-brand-blue text-brand-blue"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <UserSearch className="w-4 h-4" />
          Sourcing
        </button>
        <button
          onClick={() => setActiveTab("chat")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "chat"
              ? "border-brand-blue text-brand-blue"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          Chat
          {threads.length > 0 && (
            <span className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">
              {threads.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("invites")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "invites"
              ? "border-brand-blue text-brand-blue"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <UserCheck className="w-4 h-4" />
          Convites enviados
          {invites.length > 0 && (
            <span className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5">
              {invites.length}
            </span>
          )}
        </button>
      </div>

      {/* Sourcing tab */}
      {activeTab === "sourcing" && <SourcingLinkedInPage embedded />}

      {/* Invites tab */}
      {activeTab === "invites" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {/* Header with sync button */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/60">
            <p className="text-xs text-slate-500">
              O status é atualizado automaticamente via webhook. Use "Sincronizar" se o aceite não aparecer.
            </p>
            <button
              onClick={handleSyncInvites}
              disabled={syncingInvites}
              className="flex items-center gap-1.5 text-xs font-medium text-brand-blue hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncingInvites ? "animate-spin" : ""}`} />
              {syncingInvites ? "Sincronizando…" : "Sincronizar com Synkly"}
            </button>
          </div>
          {loadingInvites ? (
            <div className="p-10 text-center text-sm text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Carregando convites…
            </div>
          ) : invites.length === 0 ? (
            <div className="p-10 text-center space-y-2">
              <UserCheck className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="text-sm font-medium text-slate-600">Nenhum convite enviado ainda.</p>
              <p className="text-xs text-slate-400">
                Envie convites de conexão pela tela de Sourcing — eles aparecerão aqui com o status de cada um.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Candidato</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Cargo</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Enviado em</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invites.map((inv) => {
                    const st = INVITE_STATUS[inv.status] || INVITE_STATUS.pending;
                    const Icon = st.icon;
                    const isAccepting = acceptingInvite === inv.id;
                    return (
                      <tr key={inv.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-blue-50 text-brand-blue flex items-center justify-center shrink-0">
                              <Linkedin className="w-3.5 h-3.5" />
                            </div>
                            <div>
                              {inv.attendee_profile_url ? (
                                <a
                                  href={inv.attendee_profile_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-slate-900 hover:text-brand-blue hover:underline"
                                >
                                  {inv.attendee_name || "Sem nome"}
                                </a>
                              ) : (
                                <span className="font-medium text-slate-900">{inv.attendee_name || "Sem nome"}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 max-w-[220px] truncate">
                          {inv.attendee_headline || "—"}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                          {inv.created_at
                            ? new Date(inv.created_at).toLocaleString("pt-BR", {
                                day: "2-digit", month: "2-digit", year: "2-digit",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${st.cls}`}>
                            <Icon className="w-3 h-3" />
                            {st.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {inv.status === "pending" ? (
                            <button
                              onClick={() => handleManualAccept(inv)}
                              disabled={isAccepting}
                              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isAccepting
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <CheckCircle2 className="w-3 h-3" />}
                              {isAccepting ? "Marcando…" : "Marcar aceito"}
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Chat tab */}
      {activeTab === "chat" && <div className="bg-white border border-slate-200 rounded-lg overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-[500px]">
        {/* Threads list */}
        <aside className="border-r border-slate-200 bg-slate-50/40 overflow-y-auto max-h-[700px]">
          {!loading && threads.length > 0 && (
            <button
              type="button"
              onClick={() => setSortByInterest((v) => !v)}
              data-testid="toggle-sort-by-interest"
              className={`w-full px-3 py-2 text-[11px] uppercase tracking-wide font-semibold flex items-center justify-between border-b transition-colors ${
                sortByInterest
                  ? "bg-rose-50 text-rose-700 border-rose-100"
                  : "bg-white text-slate-500 hover:bg-slate-100 border-slate-100"
              }`}
              title="Reordenar com quentes no topo"
            >
              <span className="flex items-center gap-1.5">
                {sortByInterest ? <Flame className="w-3 h-3" /> : <ArrowUpDown className="w-3 h-3" />}
                {sortByInterest ? "Quentes no topo" : "Ordenar por interesse"}
              </span>
              <span className="text-[10px] text-slate-400 normal-case tracking-normal">
                {sortByInterest ? "ativo" : "off"}
              </span>
            </button>
          )}
          {loading ? (
            <div className="p-6 text-center text-sm text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Carregando…
            </div>
          ) : threads.length === 0 ? (
            <div className="p-8 text-center space-y-2">
              <Inbox className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-700">Nenhuma conversa ainda.</p>
              <p className="text-xs text-slate-500 leading-relaxed">
                Conversas aparecem aqui conforme InMails forem enviados ou recebidos
                pelo GridRecruiter após a conexão da conta LinkedIn.
              </p>
              <p className="text-xs text-slate-400 leading-relaxed mt-1">
                Histórico anterior à conexão não é importado — apenas novas mensagens
                são sincronizadas em tempo real.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {sortedThreads.map((t) => (
                <li
                  key={t.chat_id}
                  onClick={() => loadThread(t.chat_id)}
                  data-testid={`thread-${t.chat_id}`}
                  className={`p-3 cursor-pointer hover:bg-white transition-colors ${
                    t.chat_id === activeChatId ? "bg-white border-l-4 border-brand-blue" : ""
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    {t.attendee_picture_url ? (
                      <img
                        src={t.attendee_picture_url}
                        alt={t.attendee_name}
                        className="w-9 h-9 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-blue-50 text-brand-blue flex items-center justify-center shrink-0">
                        <Linkedin className="w-4 h-4" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {t.attendee_name || "Sem nome"}
                        </p>
                        {t.is_inmail && (
                          <span className="text-[9px] uppercase tracking-wide font-bold text-violet-600 bg-violet-50 px-1 rounded">
                            InMail
                          </span>
                        )}
                        <InterestBadge level={t.interest_level} reason={t.interest_reason} />
                      </div>
                      {t.attendee_headline && (
                        <p className="text-[10px] text-slate-500 truncate">
                          {t.attendee_headline}
                        </p>
                      )}
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                        {t.last_message_from_me && <span className="text-slate-400">Você: </span>}
                        {t.last_message_text || "(sem mensagens ainda)"}
                      </p>
                    </div>
                    {t.unread_count > 0 && (
                      <div className="w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                        {t.unread_count}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Active conversation */}
        <main className="flex flex-col">
          {!activeChatId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-10">
              <MessageSquare className="w-10 h-10 mb-3" />
              <p className="text-sm">Selecione uma conversa à esquerda</p>
            </div>
          ) : (
            <>
              {/* Header */}
              {activeThread && (
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 bg-white">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {activeThread.attendee_name || "Sem nome"}
                      </span>
                      <InterestBadge
                        level={activeThread.interest_level}
                        reason={activeThread.interest_reason}
                      />
                    </div>
                    {activeThread.attendee_headline && (
                      <div className="text-xs text-slate-500 truncate">
                        {activeThread.attendee_headline}
                      </div>
                    )}
                    {activeThread.interest_reason && (
                      <div className="text-[11px] text-slate-400 truncate mt-0.5" title={activeThread.interest_reason}>
                        Sinal Maya: {activeThread.interest_reason}
                      </div>
                    )}
                  </div>
                  <Button
                    data-testid="thread-maya-toggle"
                    size="sm"
                    variant="outline"
                    onClick={handleToggleMaya}
                    className={
                      activeThread.maya_enabled
                        ? "text-violet-700 border-violet-200 hover:bg-violet-50"
                        : "text-slate-500 border-slate-200"
                    }
                  >
                    {activeThread.maya_enabled ? (
                      <>
                        <Bot className="w-3.5 h-3.5 mr-1.5" />
                        Maya ativa
                      </>
                    ) : (
                      <>
                        <BotOff className="w-3.5 h-3.5 mr-1.5" />
                        Maya pausada
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Maya status banner (iter75) — explains why a reply didn't
                  happen, with a "tentar de novo" affordance for the recruiter */}
              {activeThread && MAYA_STATUS_STYLES[activeThread.last_maya_status] && (
                <div
                  data-testid="maya-status-banner"
                  className={`px-5 py-2 text-xs border-b flex items-start gap-2 ${MAYA_STATUS_STYLES[activeThread.last_maya_status].cls}`}
                >
                  <span className="text-base leading-none">
                    {MAYA_STATUS_STYLES[activeThread.last_maya_status].emoji}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">
                      Maya:{" "}
                      {MAYA_STATUS_STYLES[activeThread.last_maya_status].label}
                    </div>
                    {activeThread.last_maya_error && (
                      <div className="text-[11px] mt-0.5 opacity-80">
                        {activeThread.last_maya_error}
                      </div>
                    )}
                  </div>
                  {/* Retry button: shown for failures (not for success/handoff) */}
                  {["generation_failed", "send_failed"].includes(
                    activeThread.last_maya_status,
                  ) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid="maya-retry-btn"
                      onClick={handleMayaRetry}
                      disabled={mayaRetrying}
                      className="text-[11px] h-7 px-2 hover:bg-white/50"
                    >
                      {mayaRetrying ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        "Tentar de novo"
                      )}
                    </Button>
                  )}
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/30 min-h-[300px] max-h-[500px]">
                {loadingMessages ? (
                  <div className="text-center text-sm text-slate-500 py-10">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Carregando mensagens…
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-sm text-slate-500 py-10">
                    Sem mensagens nesta conversa ainda.
                  </div>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={`flex ${
                        m.direction === "outbound" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-3.5 py-2 text-sm ${
                          m.direction === "outbound"
                            ? "bg-brand-blue text-white"
                            : "bg-white border border-slate-200 text-slate-900"
                        }`}
                      >
                        {m.subject && (
                          <div className="text-[11px] font-bold uppercase opacity-70 mb-1 pb-1 border-b border-current/20">
                            {m.subject}
                          </div>
                        )}
                        <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
                        <div
                          className={`text-[10px] mt-1 ${
                            m.direction === "outbound" ? "text-blue-100" : "text-slate-400"
                          } flex items-center gap-1`}
                        >
                          {m.is_from_maya && (
                            <Sparkles className="w-2.5 h-2.5" />
                          )}
                          {m.is_from_maya && "Maya · "}
                          {new Date(m.created_at).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Composer */}
              <div className="p-3 border-t border-slate-200 bg-white">
                <div className="flex gap-2">
                  <Textarea
                    data-testid="thread-reply-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Responder manualmente (vai pausar a Maya nesta conversa)…"
                    rows={2}
                    className="flex-1 resize-none text-sm"
                  />
                  <Button
                    data-testid="thread-send-button"
                    onClick={handleSend}
                    disabled={!draft.trim() || sending}
                    className="bg-brand-blue hover:bg-brand-blue-dark text-white self-end"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  Cmd/Ctrl + Enter para enviar
                </p>
              </div>
            </>
          )}
        </main>
      </div>}
    </div>
  );
};

export default LinkedInInboxPage;
